import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes } from "react-router-dom";
import { PlayCircle, BookOpen, Bookmark, CreditCard, User, ExternalLink, Video } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";
import { Progress } from "@/components/ui/progress";
import { VideoCard, Empty } from "@/components/ui-lux";
import { CourseCard } from "@/pages/Courses";
import { CoachingInbox } from "@/components/CoachingInbox";

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const BookZoom = ({ url }) => (
  <section className="card-lux p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 border-[#D4AF37]/40" data-testid="coaching-booking-card">
    <div>
      <span className="eyebrow">Your coaching connection</span>
      <h2 className="font-serif text-2xl font-semibold text-white">Book your live coaching session</h2>
      <p className="text-sm text-slate-400 mt-2">Pick a time that works for you and meet with Todd's coaching team on Zoom.</p>
    </div>
    <a href={url} target="_blank" rel="noopener noreferrer" className="btn-gold shrink-0" data-testid="book-zoom-btn"><Video size={16} /> Book your Zoom session</a>
  </section>
);

const Overview = ({ d }) => (
  <div className="space-y-14">
    {d.coaching_booking_url && <BookZoom url={d.coaching_booking_url} />}
    {d.coaching_booking_url && <CoachingInbox />}
    <section data-testid="resume-section">
      <h2 className="font-serif text-2xl font-semibold text-white mb-5">Continue where you left off</h2>
      {d.resume.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {d.resume.map((r, i) => (
            <Link key={i} to={r.type === "video" ? `/videos/${r.slug}` : `/learn/${r.slug}?lesson=${r.lesson_id}`} className="card-lux flex gap-4 p-3 items-center" data-testid={`resume-item-${i}`}>
              <img src={r.thumbnail} alt="" className="w-28 aspect-video object-cover rounded-md" />
              <div className="min-w-0"><p className="text-sm text-white font-medium line-clamp-2">{r.title}</p><p className="text-xs text-slate-500 mt-1">{r.course_title || "Teaching"} · {fmtTime(r.position || 0)}</p></div>
            </Link>
          ))}
        </div>
      ) : <Empty text="Start a teaching or lesson and it will appear here." />}
    </section>
    <MyCourses d={d} />
    <section>
      <h2 className="font-serif text-2xl font-semibold text-white mb-5">New teachings</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{d.new_videos.map((v) => <VideoCard key={v.id} v={v} wide />)}</div>
    </section>
    {d.recommended_courses.length > 0 && (
      <section data-testid="recommended-section">
        <h2 className="font-serif text-2xl font-semibold text-white mb-5">Recommended for you</h2>
        <div className="grid md:grid-cols-3 gap-8">{d.recommended_courses.map((c) => <CourseCard key={c.id} c={c} />)}</div>
      </section>
    )}
  </div>
);

const MyCourses = ({ d }) => (
  <section data-testid="my-courses-section">
    <h2 className="font-serif text-2xl font-semibold text-white mb-5">My courses</h2>
    {d.courses.length ? (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {d.courses.map((c) => (
          <Link key={c.id} to={`/learn/${c.slug}`} className="card-lux overflow-hidden" data-testid={`my-course-${c.slug}`}>
            <img src={c.cover_image} alt="" className="w-full aspect-video object-cover" />
            <div className="p-5">
              <p className="text-white font-semibold">{c.title}</p>
              <div className="flex items-center gap-3 mt-4"><Progress value={c.progress.percent} className="h-1.5 bg-white/10 [&>div]:bg-[#D4AF37]" /><span className="text-xs text-gold-light font-accent">{c.progress.percent}%</span></div>
              <p className="text-xs text-slate-500 mt-2">{c.progress.completed} of {c.progress.total} lessons complete</p>
            </div>
          </Link>
        ))}
      </div>
    ) : <Empty text={<>You're not enrolled in a course yet. <Link to="/courses" className="text-gold-light underline">Browse courses</Link></>} />}
  </section>
);

const Bookmarks = ({ d }) => {
  const to = (b) => ({ video: `/videos/${b.slug}`, course: `/courses/${b.slug}`, post: `/blog/${b.slug}`, resource: "/resources" }[b.item_type]);
  return d.bookmarks.length ? (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="bookmarks-list">
      {d.bookmarks.map((b) => (
        <Link key={b.id} to={to(b)} className="card-lux overflow-hidden" data-testid={`bookmark-${b.slug}`}>
          <img src={b.thumbnail || b.cover_image} alt="" className="w-full aspect-video object-cover" />
          <div className="p-5"><p className="text-xs uppercase tracking-widest text-gold-light font-accent">{b.item_type}</p><p className="text-white mt-1">{b.title}</p></div>
        </Link>
      ))}
    </div>
  ) : <Empty text="Save teachings with the bookmark button to find them here." />;
};

const Billing = () => {
  const { user, refresh } = useAuth();
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/billing/history").then((r) => setHist(r.data)); }, []);
  const call = async (path, redirect) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, { origin_url: window.location.origin });
      if (redirect) window.location.href = data.url; else { toast.success("Partnership updated"); refresh(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-10">
      <div className="card-lux p-8 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="membership-status-card">
        <div>
          <p className="eyebrow">Partnership</p>
          <p className="font-serif text-3xl font-semibold text-white" data-testid="membership-tier-name">{user.partner_amount ? `$${user.partner_amount.toLocaleString()} / month` : "Not a partner yet"}</p>
          <p className="text-sm text-slate-400 mt-2" data-testid="membership-status-text">
            {user.subscription_status ? `Monthly gift: ${user.subscription_status}` : "Every teaching on this site is free. Partner monthly to support the ministry."}
            {user.cancel_at_period_end && ` · Cancels ${user.current_period_end ? fmtDate(user.current_period_end) : "at period end"}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/partnership" className="btn-gold" data-testid="upgrade-btn">{user.partner_amount ? "Change amount" : "Become a partner"}</Link>
          {user.subscription_status === "active" && (user.cancel_at_period_end
            ? <button disabled={busy} onClick={() => call("/billing/resume")} className="btn-outline" data-testid="resume-subscription-btn">Resume</button>
            : <button disabled={busy} onClick={() => call("/billing/cancel")} className="btn-outline" data-testid="cancel-subscription-btn">Cancel</button>)}
          {user.subscription_status && <button disabled={busy} onClick={() => call("/billing/portal", true)} className="btn-outline" data-testid="billing-portal-btn"><ExternalLink size={15} />Manage billing</button>}
        </div>
      </div>
      <div>
        <h2 className="font-serif text-2xl font-semibold text-white mb-5">Payment history</h2>
        {hist.length ? (
          <div className="overflow-x-auto card-lux"><table className="w-full text-sm" data-testid="payment-history-table">
            <thead className="text-left text-slate-500 text-xs uppercase tracking-wider"><tr>{["Date", "Item", "Amount", "Status"].map((h) => <th key={h} className="p-4">{h}</th>)}</tr></thead>
            <tbody>{hist.map((h) => <tr key={h.id} className="border-t border-white/5 text-slate-300"><td className="p-4">{fmtDate(h.created_at)}</td><td className="p-4">{h.label}</td><td className="p-4">${h.amount?.toFixed(2)} {h.currency?.toUpperCase()}</td><td className="p-4 capitalize">{h.payment_status}</td></tr>)}</tbody>
          </table></div>
        ) : <Empty text="No payments yet." />}
      </div>
    </div>
  );
};

const Account = () => {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user.name);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const saveName = async (e) => { e.preventDefault(); try { await api.put("/me/account", { name }); toast.success("Saved"); refresh(); } catch (er) { toast.error(errMsg(er)); } };
  const savePw = async (e) => { e.preventDefault(); try { await api.post("/me/password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); } catch (er) { toast.error(errMsg(er)); } };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={saveName} className="card-lux p-8 space-y-5" data-testid="account-form">
        <p className="eyebrow">Profile</p>
        <div><label className="label" htmlFor="acc-name">Name</label><input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} className="field" data-testid="account-name-input" /></div>
        <div><label className="label">Email</label><p className="text-slate-300 text-sm">{user.email}</p></div>
        <button className="btn-gold" data-testid="account-save-btn">Save</button>
      </form>
      <form onSubmit={savePw} className="card-lux p-8 space-y-5" data-testid="password-form">
        <p className="eyebrow">Password</p>
        <input type="password" placeholder="Current password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="field" data-testid="current-password-input" />
        <input type="password" placeholder="New password (8+ characters)" minLength={8} required value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="field" data-testid="new-password-input" />
        <button className="btn-outline" data-testid="password-save-btn">Change password</button>
      </form>
    </div>
  );
};

const TABS = [["", "Overview", PlayCircle], ["courses", "My Courses", BookOpen], ["bookmarks", "Bookmarks", Bookmark], ["billing", "Partnership & Billing", CreditCard], ["account", "Account", User]];

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useSEO({ title: "Dashboard" });
  useEffect(() => { api.get("/me/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  return (
    <div className="pt-36 container-x" data-testid="dashboard-page">
      <span className="eyebrow">{user.partner_amount ? "Monthly partner" : "Member"}</span>
      <h1 className="h1 !text-4xl sm:!text-5xl lg:!text-6xl" data-testid="dashboard-welcome">Welcome to Your Transformation Journey.</h1>
      <p className="lead mt-3">Good to see you, {user.name?.split(" ")[0]}.</p>
      <nav className="flex gap-2 overflow-x-auto no-scrollbar mt-10 mb-12 border-b border-white/10" aria-label="Dashboard">
        {TABS.map(([to, label, Icon]) => (
          <NavLink key={to} to={`/dashboard${to ? `/${to}` : ""}`} end className={({ isActive }) => `flex items-center gap-2 shrink-0 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${isActive ? "border-[#D4AF37] text-white" : "border-transparent text-slate-400 hover:text-white"}`} data-testid={`dashboard-tab-${to || "overview"}`}><Icon size={15} />{label}</NavLink>
        ))}
      </nav>
      {!d ? <div className="h-60" /> : (
        <Routes>
          <Route index element={<Overview d={d} />} />
          <Route path="courses" element={<MyCourses d={d} />} />
          <Route path="bookmarks" element={<Bookmarks d={d} />} />
          <Route path="billing" element={<Billing />} />
          <Route path="account" element={<Account />} />
        </Routes>
      )}
    </div>
  );
}
